import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { headerSty, contentSty } from "../table-styles";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import {
  formatDecimalNumber,
  formatTextType,
  formatTime,
  getUrlPathId,
  limitToSeven,
} from "../../utils/url-id";
import {
  FacilityEmployeeProps,
  FacilityEmploymentProps,
} from "../../interface/facility";

import { Pagination } from "../Pagination";
import { EmploymentDetailsModal } from "./modal-contents";
import EmploymentStats from "./employmentStats";
import { FilterEmploymentInputs } from "./FiltterInputs";

export default function EmployeeFacility({
  employment,
  loading,
  stats,
  pagination_state,
  setFilterData,
  setPagination_state,
  handleOpenSelectedModalData,
}: FacilityEmployeeProps) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: "#93A5D8", fontWeight: 400 };
  const contentStyles = { ...contentSty };
  const [compare, setCompare] = React.useState(false);

  return (
    <>
      <EmploymentStats stats={stats} />
      <Box
        sx={{
          background: "#FAFBFC",
          m: 2,
          py: 3,
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="subtitle2"
            sx={{ ...headerSty, textAlign: "left", px: 3 }}
          >
            Facility Employees
          </Typography>

          <Button onClick={() => setCompare(!compare)}>
            {compare ? <FilterAltOffIcon /> : <FilterAltIcon />}
          </Button>
        </Stack>
        {compare && (
          <FilterEmploymentInputs
            loading={loading}
            setFilterData={setFilterData}
          />
        )}
        <TableContainer
          component={Paper}
          sx={{
            px: 2,
            py: 3,
            width: "98%",
            mx: "auto",
            minHeight: 300,
            background: "#FAFBFC",
            borderRadius: 1,
            boxShadow: "none",
          }}
        >
          <Table
            sx={{ width: "98%", mx: "auto" }}
            size="small"
            aria-label="a dense table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headStyles }}>ID</TableCell>
                <TableCell sx={{ ...headStyles }}>Name</TableCell>
                <TableCell sx={{ ...headStyles }}>Medic Type</TableCell>
                <TableCell sx={{ ...headStyles }}>Job Type</TableCell>
                <TableCell sx={{ ...headStyles }}>Employment Type </TableCell>
                <TableCell sx={{ ...headStyles }}>Status </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                employment.map((row: FacilityEmploymentProps) => (
                  <TableRow
                    key={row?._id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    onClick={() => handleOpenSelectedModalData(row)}
                  >
                    <TableCell
                      sx={{
                        ...contentStyles,
                        textAlign: "left",
                        padding: "12px",
                      }}
                      component="th"
                      scope="row"
                    >
                      {limitToSeven(row._id) ?? "-"}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {row?.medic_info?.name}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.medic_info?.medic_type)}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.job_info?.job_type)}
                    </TableCell>

                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.job_info?.employment_type)}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      <FacilityEmpStatus status={row?.status} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {employment?.length === 0 && !loading && (
            <Box
              sx={{
                height: 200,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
              >
                No Employment found
              </Typography>
            </Box>
          )}
          {loading && (
            <Box sx={{ width: "96%", mx: "auto" }}>
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
            </Box>
          )}
        </TableContainer>

        <Box
          sx={{
            background: "#FAFBFC",
            padding: "16px",
            borderRadius: "16px",
          }}
        >
          <Pagination
            paginationState={pagination_state}
            setPaginationState={setPagination_state}
          />
        </Box>
      </Box>
    </>
  );
}

export const FacilityEmpStatus: React.FC<{
  status: "inactive" | "closed" | "active" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "active":
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
        };
      case "closed":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "inactive":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};

export const EmploymentEmpStatus: React.FC<{
  status: "inactive" | "active" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "inactive":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "active":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.light,
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};
